/**
 * Document Upload Component - Glass Morphism Design
 * Drag-and-drop or click to upload documents for RAG
 * Supports: PDF, DOCX, TXT, MD, HTML, CSV
 */
import { useRef, useState } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import type { ChunkingStrategy } from '../../types';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

const SUPPORTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/html',
  'text/csv',
];

const SUPPORTED_EXTENSIONS = '.pdf,.docx,.txt,.md,.html,.htm,.csv';

const CHUNKING_STRATEGIES: Array<{ value: ChunkingStrategy; label: string; description: string }> = [
  {
    value: 'SEMANTIC',
    label: 'Semantic (Recommended)',
    description: 'Smart sentence boundary detection using NLP',
  },
  {
    value: 'MARKDOWN_AWARE',
    label: 'Markdown-Aware',
    description: 'Preserves markdown structure and headings',
  },
  {
    value: 'RECURSIVE',
    label: 'Recursive',
    description: 'Hierarchical chunking (paragraphs → sentences)',
  },
  {
    value: 'SLIDING_WINDOW',
    label: 'Sliding Window',
    description: 'Overlapping windows for context preservation',
  },
  {
    value: 'FIXED_SIZE',
    label: 'Fixed Size',
    description: 'Traditional character-based chunking',
  },
];

export function DocumentUpload() {
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [chunkingStrategy, setChunkingStrategy] = useState<ChunkingStrategy>('SEMANTIC');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadDocument, uploadDocuments, uploadProgress, isLoading } = useDocumentStore();

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const supportedFiles = files.filter((file) => SUPPORTED_TYPES.includes(file.type));

    if (supportedFiles.length > 0) {
      await handleFileUpload(supportedFiles);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(Array.from(files));
    }
    // Reset input so same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (files: File[]) => {
    try {
      if (files.length === 1 && files[0]) {
        await uploadDocument(files[0], chunkingStrategy);
      } else {
        await uploadDocuments(files, chunkingStrategy);
      }
    } catch (error: any) {
      // Error is already set in store
      console.error('Upload failed:', error.message);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      className="rounded-2xl p-6 border"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: THEME.border,
      }}
    >
      <h2 className="text-xl font-semibold mb-4" style={{ color: THEME.textMain }}>
        Upload Document
      </h2>

      {/* Upload Area */}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{
          borderColor: isDragging ? THEME.accentColor : THEME.border,
          backgroundColor: isDragging ? THEME.accentSoft : 'transparent',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORTED_EXTENSIONS}
          multiple
          onChange={handleFileSelect}
          disabled={isLoading}
          className="hidden"
        />

        <Icons.Upload
          size={48}
          className="mx-auto mb-4"
          style={{ color: isDragging ? THEME.accentColor : THEME.textMuted }}
        />

        <p className="mb-2" style={{ color: THEME.textMain }}>
          {isLoading ? 'Uploading...' : 'Drop files here or click to browse'}
        </p>
        <p className="text-sm mb-1" style={{ color: THEME.textSecondary }}>
          Supports: PDF, DOCX, TXT, MD, HTML, CSV
        </p>
        <p className="text-xs" style={{ color: THEME.textMuted }}>
          Max 50MB per file • Multiple files supported
        </p>

        {/* Upload Progress */}
        {isLoading && uploadProgress > 0 && (
          <div className="mt-4">
            <div
              className="w-full rounded-full h-2"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              <div
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${uploadProgress}%`,
                  background: THEME.accent,
                }}
              />
            </div>
            <p className="text-sm mt-2" style={{ color: THEME.textSecondary }}>
              {uploadProgress}%
            </p>
          </div>
        )}
      </div>

      {/* Advanced Settings */}
      <div className="mt-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm transition-colors"
          style={{ color: THEME.textSecondary }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = THEME.textMain;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = THEME.textSecondary;
          }}
        >
          <Icons.ChevronDown
            size={16}
            className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
          />
          <span>Advanced Settings</span>
        </button>

        {showAdvanced && (
          <div
            className="mt-3 p-4 rounded-xl border"
            style={{
              backgroundColor: THEME.bgSurface,
              borderColor: THEME.border,
            }}
          >
            <label className="block text-sm font-medium mb-2" style={{ color: THEME.textMain }}>
              Chunking Strategy
            </label>
            <select
              value={chunkingStrategy}
              onChange={(e) => setChunkingStrategy(e.target.value as ChunkingStrategy)}
              disabled={isLoading}
              className="w-full px-3 py-2 rounded-lg border text-sm transition-all focus:outline-none disabled:opacity-50"
              style={{
                backgroundColor: THEME.glassLight,
                borderColor: THEME.border,
                color: THEME.textMain,
              }}
            >
              {CHUNKING_STRATEGIES.map((strategy) => (
                <option key={strategy.value} value={strategy.value}>
                  {strategy.label}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs" style={{ color: THEME.textMuted }}>
              {CHUNKING_STRATEGIES.find((s) => s.value === chunkingStrategy)?.description}
            </p>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="mt-4 space-y-2 text-sm" style={{ color: THEME.textSecondary }}>
        <p className="flex items-start gap-2">
          <Icons.Check size={20} className="flex-shrink-0 mt-0.5" style={{ color: THEME.accentColor }} />
          <span>
            Documents are intelligently chunked and indexed using semantic embeddings.
            Query them naturally in the RAG Chat section below.
          </span>
        </p>
      </div>
    </div>
  );
}
